import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CAppSubTitle from '../../../../app/components/CAppSubTitle';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import IFund from '../../interfaces/fund/IFund';
import IFundFilters from '../../interfaces/fund/IFundFilters';
import IFundReportingPeriod from '../../interfaces/IFundReportingPeriod';
import investmentFundLang from '../../langs/investmentFundLang';
import useInvestFundAssets from '../../hooks/fund/useInvestFundAssets';
import { Box, Stack, useTheme } from '@mui/material';
import _ from 'lodash';
import { GridRowsProp, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import AppFormatAmount from '../../../../app/features/format/AppFormatAmount';
import styleVariables from '../../../../app/styles/styleVariables';
import langInvestWordLang from '../../langs/langInvestWordLang';
import CAppDataGrid from '../../../../app/components/CAppDataGrid';
import AppFormatDate from '../../../../app/features/format/AppFormatDate';
import langInvestFundAssetsTable from '../../langs/langInvestFundAssetsTable';
import isoCountryCurrency from 'i18n-iso-countries';
import isoCountryCurrencyEN from 'i18n-iso-countries/langs/en.json';
import isoCountryCurrencyFR from 'i18n-iso-countries/langs/fr.json';

isoCountryCurrency.registerLocale(isoCountryCurrencyFR);
isoCountryCurrency.registerLocale(isoCountryCurrencyEN);

type VInvestFundAssetsTableProps = {
	fund: IFund;
	filters: IFundFilters;
};

const VInvestFundAssetsTable = (props: VInvestFundAssetsTableProps) => {
	const { fund } = props;

	const { t, i18n } = useTranslation();

	const { fundAssets } = useInvestFundAssets(fund.id);

	const [reportingPeriod, setReportingPeriod] = useState<IFundReportingPeriod | null | undefined>(undefined);
	useEffect(() => {
		if (fundAssets && reportingPeriod === undefined) {
			const reportingPeriodsTmp = _.uniqBy(
				fundAssets.map((x) => x.investment?.reportingPeriod),
				'tag',
			);
			if (reportingPeriodsTmp.length === 1) {
				setReportingPeriod(reportingPeriodsTmp[0]);
			} else {
				setReportingPeriod(null);
			}
		}
	}, [fundAssets, reportingPeriod]);

	const [rows, setRows] = useState<GridRowsProp | null>(null);
	useEffect(() => {
		if (fundAssets) {
			setRows(
				_.orderBy(
					fundAssets.map((x, index) => {
						return {
							id: index,
							assetName: x.name,
							assetImgSrc: x.imgSrc,
							assetCountry: x.countryCode,
							assetInvestmentDate: x.investment?.date,
							assetMaturityDate: x.investment?.maturityDate,
							kpiContributedCapital: x.investment?.contributedCapital,
							kpiOutstandingCapital: x.investment?.outstandingCapital,
						};
					}),
					['investment.date'],
					['desc'],
				),
			);
		}
	}, [fundAssets]);

	const columns: GridColDef[] = [
		{
			field: 'assetName',
			headerName: t(langInvestFundAssetsTable.asset_name.tag),
			minWidth: 250,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
						{fundAssets?.some((x) => x.imgSrc) ? (
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="center"
								spacing={0}
								sx={{
									padding: '2px',
									width: '80px',
									height: '40px',
									backgroundColor: 'white',
									overflow: 'hidden',
									borderRadius: '10px',
									border: `${params.row.assetImgSrc ? '1' : '0'}px solid ${
										styleVariables.greyLightColor
									}`,
								}}
							>
								<Box
									component="img"
									sx={{
										height: '36px',
										maxWidth: '80px',

										// marginLeft: theme.palette.mode !== 'dark' ? '-10px' : '',
									}}
									src={params.row.assetImgSrc}
								/>
							</Stack>
						) : (
							<></>
						)}
						<Box>{params.value}</Box>
					</Stack>
				);
			},
		},
		{
			field: 'assetCountry',
			headerName: t(langInvestFundAssetsTable.country_name.tag),
			width: 180,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) =>
				params.value ? isoCountryCurrency.getName(params.value, i18n.language) : null,
		},
		{
			field: 'assetInvestmentDate',
			headerName: t(langInvestFundAssetsTable.investment_date.tag),
			width: 200,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) => <AppFormatDate date={params.value} />,
		},
		{
			field: 'assetMaturityDate',
			headerName: t(langInvestFundAssetsTable.asset_maturity_date.tag),
			width: 180,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params: GridRenderCellParams) => <AppFormatDate date={params.value} />,
		},
		{
			field: 'kpiContributedCapital',
			headerName: t(langInvestFundAssetsTable.contributed_capital.tag),
			width: 150,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<AppFormatAmount
						value={params.value}
						addSpace
						textIfNull={t(langInvestWordLang.nc.tag)}
						currency={fund.currency}
					/>
				);
			},
		},
		{
			field: 'kpiOutstandingCapital',
			headerName: t(langInvestFundAssetsTable.outstanding_capital.tag),
			width: 150,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<AppFormatAmount
						value={params.value}
						addSpace
						textIfNull={t(langInvestWordLang.nc.tag)}
						currency={fund.currency}
					/>
				);
			},
		},
	];

	const theme = useTheme();

	return (
		<>
			<CAppSubTitle
				title={t(investmentFundLang.title_assets.tag)}
				subtitle={
					reportingPeriod
						? `${t(langInvestFundAssetsTable.subtitle_periode_reporting.tag)} : ${reportingPeriod.name}`
						: null
				}
				icon={<AppCustomIcon tag="fund_assets" className="icon" />}
			/>
			<Box id="fund-assets-items">
				{rows ? (
					<Box>
						<CAppDataGrid
							sx={{
								marginLeft: '-10px',
								'*::-webkit-scrollbar': {
									height: '5px',
								},
								'*::-webkit-scrollbar-track': {
									backgroundColor: theme.palette.background.paper,
								},

								'*::-webkit-scrollbar-thumb': {
									backgroundColor: theme.palette.primary.main,
								},
							}}
							rows={rows}
							disableSelectionOnClick
							disableColumnSelector
							disableColumnMenu
							columns={columns}
							autoHeight
							hideFooter
						/>
					</Box>
				) : (
					''
				)}
			</Box>
		</>
	);
};

export default VInvestFundAssetsTable;
